import { useEffect, useState } from "react";

import type { Meta, StoryObj } from "@storybook/react";

import type { ButtonProps } from "@/ui/core";
import { Button, FAIconName } from "@/ui/core";
import type { ButtonSize, ButtonVariant } from "@/ui/core/buttons/Button";

const ButtonVariantValues: Record<ButtonVariant, ButtonVariant> = {
  "accent.blue": "accent.blue",
  "accent.critical": "accent.critical",
  "accent.lime": "accent.lime",
  primary: "primary",
  "primary.on_background": "primary.on_background",
  secondary: "secondary",
  tertiary: "tertiary",
  "tertiary.critical": "tertiary.critical",
  toggleable: "toggleable",
};

const ButtonSizeValues: Record<ButtonSize, ButtonSize> = {
  normal: "normal",
  small: "small",
};

const ButtonMeta: Meta<ButtonProps> = {
  title: "Core/Button",
  component: Button,
  argTypes: {
    isActive: { control: "boolean" },
    isDisabled: { control: "boolean" },
    isFullWidth: { control: "boolean" },
    isLoading: { control: "boolean" },
    leftIcon: {
      control: "select",
      options: Object.values(FAIconName),
    },
    onPress: { action: "pressed the button" },
    rightIcon: {
      control: "select",
      options: Object.values(FAIconName),
    },
    size: {
      control: "select",
      options: Object.values(ButtonSizeValues),
    },
    variant: {
      control: "select",
      options: Object.values(ButtonVariantValues),
    },
  },
  args: {
    size: "normal",
    title: "Book an appointment",
    variant: "primary",
  },
};

type Story = StoryObj<ButtonProps>;

const Primary: Story = {};
const PrimaryOnBackground: Story = {
  args: { isDisabled: true, variant: "primary.on_background" },
};

const Secondary: Story = {
  args: { variant: "secondary" },
};

const Tertiary: Story = {
  args: { variant: "tertiary" },
};

const TertiaryCritical: Story = {
  args: { variant: "tertiary.critical" },
};

const AccentLime: Story = {
  args: { variant: "accent.lime" },
};

const AccentBlue: Story = {
  args: { variant: "accent.blue" },
};

const AccentCritical: Story = {
  args: { variant: "accent.critical" },
};

const Toggleable: Story = {
  args: { variant: "toggleable" },
  // eslint-disable-next-line no-restricted-syntax -- non-arrow function used here as React FC needed for use of state hook
  render: function Render(args: ButtonProps) {
    const { isActive } = args;
    const [isToggled, setIsToggled] = useState<boolean>(true);
    useEffect(() => setIsToggled(Boolean(isActive)), [isActive]);
    return (
      <Button
        {...args}
        isActive={isToggled}
        onPress={() => setIsToggled(!isToggled)}
      />
    );
  },
};

const Small: Story = {
  args: { size: "small" },
};

const FullWidth: Story = {
  args: { isFullWidth: true },
};

const WithLeftIcon: Story = {
  args: { leftIcon: FAIconName.Calendar },
};

const WithRightIcon: Story = {
  args: { rightIcon: FAIconName.Home },
};

const WithTwoIcons: Story = {
  args: { leftIcon: FAIconName.Calendar, rightIcon: FAIconName.Home },
};

export {
  AccentBlue,
  AccentCritical,
  AccentLime,
  FullWidth,
  Primary,
  PrimaryOnBackground,
  Secondary,
  Small,
  Tertiary,
  TertiaryCritical,
  Toggleable,
  WithLeftIcon,
  WithRightIcon,
  WithTwoIcons,
};
export default ButtonMeta;
